import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  TextField,
  Typography,
} from '@shared/ui';
import { useState } from 'react';
import {
  useGetCalendarChangeRuntimeQuery,
  useSetCalendarChangeSyncMutation,
} from './api/adminCalendarChangesApi';

const errorCode = (error: unknown): string | null => {
  if (typeof error !== 'object' || error === null || !('data' in error)) return null;
  const data = error.data;
  if (typeof data !== 'object' || data === null || !('error' in data)) return null;
  return typeof data.error === 'string' ? data.error : null;
};

export function AdminCalendarChangeRuntimeControl() {
  const runtime = useGetCalendarChangeRuntimeQuery();
  const [setSync, sync] = useSetCalendarChangeSyncMutation();
  const [reason, setReason] = useState('');

  const pending = sync.isLoading || runtime.isFetching;
  const mutationErrorCode = errorCode(sync.error);
  const reasonRequired = reason.trim() === '';
  const syncEnabled = runtime.data?.sync_enabled === true;
  const canToggle = !pending && !reasonRequired && runtime.data !== undefined;

  const toggleSync = () => {
    if (!runtime.data || !canToggle) return;
    void setSync({ enabled: !syncEnabled, reason: reason.trim() });
  };

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography component="h2" variant="h6">
          Runtime control
        </Typography>
        {sync.error ? (
          <Alert severity="error" sx={{ mt: 2 }}>
            Could not update calendar sync
            {mutationErrorCode ? `: ${mutationErrorCode}` : ''}.
          </Alert>
        ) : null}
        {runtime.error ? (
          <Alert severity="error" sx={{ mt: 2 }}>
            Could not load runtime control.
          </Alert>
        ) : runtime.isLoading ? (
          <CircularProgress size={24} sx={{ mt: 2 }} />
        ) : runtime.data ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                gap: 1.5,
              }}
            >
              <Box>
                <Typography color="text.secondary" variant="caption">
                  Calendar sync
                </Typography>
                <Typography fontWeight={700} color={syncEnabled ? undefined : 'error.main'}>
                  {syncEnabled ? 'On' : 'Off'}
                </Typography>
              </Box>
              <Box>
                <Typography color="text.secondary" variant="caption">
                  Changed at
                </Typography>
                <Typography>{new Date(runtime.data.changed_at).toLocaleString()}</Typography>
              </Box>
              <Box>
                <Typography color="text.secondary" variant="caption">
                  Changed by
                </Typography>
                <Typography>{runtime.data.actor}</Typography>
              </Box>
              <Box>
                <Typography color="text.secondary" variant="caption">
                  Reason
                </Typography>
                <Typography>{runtime.data.reason}</Typography>
              </Box>
            </Box>
            <Alert severity={syncEnabled ? 'info' : 'warning'}>
              {syncEnabled
                ? 'Calendar sync is on: detected Google changes converge onto Moments appointments for every coach. Turning it off stops convergence within one poll interval, without a deploy.'
                : 'Calendar sync is off: nothing is detected and nothing is converged. There is no watch-without-writing state, so turning it on is a live change.'}
            </Alert>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '2fr auto' },
                gap: 1.5,
                alignItems: 'center',
              }}
            >
              <TextField
                disabled={pending}
                label="Change reason"
                onChange={(event) => setReason(event.target.value)}
                required
                size="small"
                value={reason}
              />
              <Button
                color={syncEnabled ? 'error' : 'primary'}
                disabled={!canToggle}
                onClick={toggleSync}
                variant="contained"
              >
                {syncEnabled ? 'Turn calendar sync off' : 'Turn calendar sync on'}
              </Button>
            </Box>
          </Box>
        ) : null}
      </CardContent>
    </Card>
  );
}
